// The ONLY data-aware module — the seam between the calendar UI and the SH-1408
// backend (`GET /Calendar/Events`). Fetches by DATE RANGE only; source/sub-type
// filtering stays client-side (useEventFilters).

import { useCallback, useEffect, useRef, useState } from 'react'
import { addDays } from 'date-fns'
import type { CalendarEvent, SourceStatus } from '../typings'
import type { CalendarEventsResponse } from '../api/calendarContract'
import { fetchCalendarRange } from '../api/fetchCalendarEvents'
import { mapCalendarEvent, toSourceStatus } from '../api/mapCalendarEvent'
import { overlapsRange } from '../eventSpan'

export type CalendarFetchRange = { start: Date; end: Date }

type CalendarEventsState = {
  /** Only events that overlap the requested range — exactly what the grid shows. */
  events: CalendarEvent[]
  sourceStatus: SourceStatus[]
  isLoading: boolean
  error: Error | null
}

export type UseCalendarEventsResult = CalendarEventsState & {
  /** Re-issue the request for the current range (e.g. after a load error). */
  retry: () => void
  /** Silent refetch of the current range: keeps the grid, flags Sales Center on failure.
   *  After a failed first load there is nothing to keep, so it falls back to `retry`. */
  refresh: () => void
}

// All-day events are pinned to their UTC date, which can sit up to a day either
// side of the device's local-midnight range boundaries, so the query is padded
// and the result trimmed back to the range on the client.
const ALL_DAY_PAD_DAYS = 1

// Reference-stable empties so downstream memos (CalendarPage's visibleEvents)
// don't churn while the calendar is empty.
const NO_EVENTS: CalendarEvent[] = []
const NO_SOURCE_STATUS: SourceStatus[] = []
// What every render sees until a result for the CURRENT request settles —
// including the first paint, before the effect has even issued the fetch.
const LOADING: CalendarEventsState = {
  events: NO_EVENTS,
  sourceStatus: NO_SOURCE_STATUS,
  isLoading: true,
  error: null,
}

export const REFRESH_FAILED_MESSAGE =
  "Couldn't refresh. Showing the last loaded events."

const requestRange = (start: Date, end: Date, signal: AbortSignal) =>
  fetchCalendarRange({
    start: addDays(start, -ALL_DAY_PAD_DAYS),
    end: addDays(end, ALL_DAY_PAD_DAYS),
    signal,
  })

function toLoadedState(
  res: Pick<CalendarEventsResponse, 'events' | 'errors'>,
  start: Date,
  end: Date,
): CalendarEventsState {
  return {
    events: res.events
      .map(mapCalendarEvent)
      .filter((event) => overlapsRange(event, start, end)),
    sourceStatus: toSourceStatus(res.errors),
    isLoading: false,
    error: null,
  }
}

// An SC error already on screen (a real failure or the truncation warning) is
// more specific than "refresh failed", so only a healthy SC flips to it.
function withRefreshFailure(statuses: SourceStatus[]): SourceStatus[] {
  return statuses.map((s) =>
    s.source === 'sales_center' && s.status !== 'error'
      ? {
          source: 'sales_center',
          status: 'error',
          message: REFRESH_FAILED_MESSAGE,
        }
      : s,
  )
}

export function useCalendarEvents({
  start,
  end,
}: CalendarFetchRange): UseCalendarEventsResult {
  const [attempt, setAttempt] = useState(0)
  const [refreshTick, setRefreshTick] = useState(0)
  const retry = useCallback(() => setAttempt((n) => n + 1), [])
  // Each settled result is tagged with the request it answers. A result for any
  // other range/attempt reads as loading on the very render the range changes —
  // an effect-side reset would land one commit late, briefly showing the old
  // range's events under the new heading.
  const requestKey = `${start.getTime()}:${end.getTime()}:${attempt}`
  const [settled, setSettled] = useState<{
    key: string
    state: CalendarEventsState
  } | null>(null)
  const settledRef = useRef(settled)
  settledRef.current = settled
  const handledTickRef = useRef(0)
  const settledKey = settled?.key

  const refresh = useCallback(() => {
    if (settledRef.current?.state.error) {
      setAttempt((n) => n + 1)
      return
    }
    setRefreshTick((n) => n + 1)
  }, [])

  useEffect(() => {
    // `cancelled` drops a superseded result even if it resolves before the abort
    // lands; the abort itself stops the superseded requests on the wire.
    let cancelled = false
    const controller = new AbortController()
    const setState = (state: CalendarEventsState) =>
      setSettled({ key: requestKey, state })

    requestRange(start, end, controller.signal)
      .then((res) => {
        if (cancelled) return
        setState(toLoadedState(res, start, end))
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setState({
          events: NO_EVENTS,
          sourceStatus: NO_SOURCE_STATUS,
          isLoading: false,
          error: err instanceof Error ? err : new Error(String(err)),
        })
      })

    return () => {
      cancelled = true
      controller.abort()
    }
    // The key captures the primitive time values + attempt, not Date identities.
  }, [requestKey])

  useEffect(() => {
    if (refreshTick === handledTickRef.current) return
    const current = settledRef.current
    // Still loading (or the range just changed): leave the tick pending so it is
    // applied as soon as this range settles, instead of being dropped.
    if (!current || current.key !== requestKey) return
    handledTickRef.current = refreshTick

    let cancelled = false
    const controller = new AbortController()
    requestRange(start, end, controller.signal)
      .then((res) => {
        if (cancelled) return
        setSettled({ key: requestKey, state: toLoadedState(res, start, end) })
      })
      .catch(() => {
        if (cancelled) return
        setSettled((latest) =>
          latest?.key === requestKey
            ? {
                key: requestKey,
                state: {
                  ...latest.state,
                  sourceStatus: withRefreshFailure(latest.state.sourceStatus),
                },
              }
            : latest,
        )
      })

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [refreshTick, requestKey, settledKey])

  const state = settled?.key === requestKey ? settled.state : LOADING
  return { ...state, retry, refresh }
}
